import React, {FC, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap-v5'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {apiErrorMessage} from '../../public/PublicModels'
import {toDateOnly} from '../calendar/CalendarModel'
import {startGoogleConnect, useGoogleStatus} from '../calendar/GoogleConnection'
import {ContactPicker, IParticipant} from '../contact/ContactPicker'
import {useMailTemplateOptions} from '../shared/useMailTemplateOptions'
import {createInterview} from './Api'

export interface IInterviewTarget {
  trackingId: string
  candidateName: string
  candidateEmail?: string | null
  jobTitle?: string | null
}

type Props = {
  target: IInterviewTarget | null
  onClose: () => void
  onCreated?: () => void
}

type FormState = {
  title: string
  date: string
  startTime: string
  endTime: string
  participants: IParticipant[]
  mailTemplateId: string
  notifyCandidate: boolean
  createMeet: boolean
  meetingLink: string
  description: string
}

const tomorrow = () => toDateOnly(new Date(Date.now() + 86400000))

const initialState = (target: IInterviewTarget | null): FormState => ({
  title: target?.jobTitle ? `${target.jobTitle} Mülakatı` : 'Mülakat',
  date: tomorrow(),
  startTime: '10:00',
  endTime: '11:00',
  participants: [],
  mailTemplateId: '',
  notifyCandidate: true,
  createMeet: false,
  meetingLink: '',
  description: '',
})

/**
 * Aday takipten mülakat planlama: tarih / saat, katılımcılar (rehber), mail şablonu ve
 * isteğe bağlı Google Meet linki. Kaydedilince takvime işlenir ve mailler gönderilir.
 */
export const CreateInterviewModal: FC<Props> = ({target, onClose, onCreated}) => {
  const [form, setForm] = useState<FormState>(initialState(target))
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({})
  const [saving, setSaving] = useState(false)
  const {options: templateOptions, loading: templatesLoading} = useMailTemplateOptions(!!target)
  const {status: google} = useGoogleStatus()

  useEffect(() => {
    if (target) {
      setForm(initialState(target))
      setErrors({})
    }
  }, [target])

  useEffect(() => {
    if (google?.connected) setForm((current) => ({...current, createMeet: true}))
  }, [google?.connected])

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((current) => ({...current, [key]: value}))

  const validate = () => {
    const found: Partial<Record<keyof FormState, string>> = {}
    if (!form.title.trim()) found.title = 'Başlık zorunludur'
    if (!form.date) found.date = 'Tarih zorunludur'
    if (!form.startTime) found.startTime = 'Başlangıç saati zorunludur'
    if (!form.endTime) found.endTime = 'Bitiş saati zorunludur'
    else if (form.startTime && form.endTime <= form.startTime) found.endTime = 'Bitiş, başlangıçtan sonra olmalıdır'
    if (form.meetingLink.trim() && !/^https?:\/\//i.test(form.meetingLink.trim())) {
      found.meetingLink = 'Link http:// veya https:// ile başlamalıdır'
    }
    setErrors(found)
    return Object.keys(found).length === 0
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!target || !validate()) return
    setSaving(true)
    try {
      const {data} = await createInterview({
        trackingId: target.trackingId,
        title: form.title.trim(),
        description: form.description.trim(),
        start: `${form.date}T${form.startTime}:00`,
        end: `${form.date}T${form.endTime}:00`,
        participants: form.participants,
        mailTemplateId: form.mailTemplateId || null,
        notifyCandidate: form.notifyCandidate,
        createMeet: form.createMeet,
        meetingLink: form.createMeet ? null : form.meetingLink.trim() || null,
      })
      const interview = data.payload
      showSuccessToast(
        form.mailTemplateId
          ? 'Mülakat planlandı, takvime işlendi ve davet mailleri gönderiliyor'
          : 'Mülakat planlandı ve takvime işlendi'
      )
      if (form.createMeet && !interview.meetLink) {
        showErrorToast('Google Meet linki oluşturulamadı; mülakat linksiz kaydedildi')
      }
      onCreated?.()
      onClose()
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Mülakat oluşturulamadı'))
    } finally {
      setSaving(false)
    }
  }

  const invalid = (key: keyof FormState) => (errors[key] ? 'is-invalid' : '')
  const feedback = (key: keyof FormState) => errors[key] && <div className='invalid-feedback'>{errors[key]}</div>

  return (
    <Modal show={!!target} onHide={onClose} size='lg' dialogClassName='modal-dialog-centered' backdrop='static'>
      <form onSubmit={handleSubmit} noValidate>
        <div className='modal-header'>
          <div>
            <h5 className='modal-title'>Mülakat Oluştur</h5>
            <div className='text-muted fs-7'>
              {target?.candidateName}
              {target?.candidateEmail ? ` · ${target.candidateEmail}` : ''}
            </div>
          </div>
          <button type='button' className='btn-close' onClick={onClose} />
        </div>
        <div className='modal-body'>
          <div className='row g-5'>
            <div className='col-12'>
              <label className='form-label fw-bold required'>Başlık</label>
              <input
                type='text'
                maxLength={255}
                className={`form-control ${invalid('title')}`}
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
              />
              {feedback('title')}
            </div>
            <div className='col-md-4'>
              <label className='form-label fw-bold required'>Tarih</label>
              <input
                type='date'
                className={`form-control ${invalid('date')}`}
                value={form.date}
                onChange={(e) => set('date', e.target.value)}
              />
              {feedback('date')}
            </div>
            <div className='col-md-4'>
              <label className='form-label fw-bold required'>Başlangıç</label>
              <input
                type='time'
                className={`form-control ${invalid('startTime')}`}
                value={form.startTime}
                onChange={(e) => set('startTime', e.target.value)}
              />
              {feedback('startTime')}
            </div>
            <div className='col-md-4'>
              <label className='form-label fw-bold required'>Bitiş</label>
              <input
                type='time'
                className={`form-control ${invalid('endTime')}`}
                value={form.endTime}
                onChange={(e) => set('endTime', e.target.value)}
              />
              {feedback('endTime')}
            </div>

            <div className='col-12'>
              <label className='form-label fw-bold'>Katılımcılar</label>
              <ContactPicker value={form.participants} onChange={(participants) => set('participants', participants)} />
              <div className='form-text'>Rehberde olmayan e-postalar mülakat kaydedilince rehbere eklenir.</div>
            </div>

            <div className='col-12'>
              <label className='form-label fw-bold'>Toplantı Linki</label>
              {google?.configured && (
                <div className='d-flex align-items-center gap-3 mb-3'>
                  {google.connected ? (
                    <label className='form-check form-check-custom form-check-solid'>
                      <input
                        type='checkbox'
                        className='form-check-input'
                        checked={form.createMeet}
                        onChange={(e) => set('createMeet', e.target.checked)}
                      />
                      <span className='form-check-label'>
                        <i className='bi bi-camera-video me-1'></i>
                        Google Meet linki oluştur ({google.googleEmail})
                      </span>
                    </label>
                  ) : (
                    <button type='button' className='btn btn-sm btn-light-primary' onClick={startGoogleConnect}>
                      <i className='bi bi-google'></i>
                      Meet linki için Google hesabını bağla
                    </button>
                  )}
                </div>
              )}
              {!form.createMeet && (
                <>
                  <input
                    type='url'
                    maxLength={255}
                    className={`form-control ${invalid('meetingLink')}`}
                    placeholder='Varsa toplantı linki (Zoom, Teams vb.) ya da boş bırakın'
                    value={form.meetingLink}
                    onChange={(e) => set('meetingLink', e.target.value)}
                  />
                  {feedback('meetingLink')}
                </>
              )}
            </div>

            <div className='col-md-7'>
              <label className='form-label fw-bold'>Mail Şablonu</label>
              <select
                className='form-select'
                value={form.mailTemplateId}
                disabled={templatesLoading}
                onChange={(e) => set('mailTemplateId', e.target.value)}
              >
                <option value=''>Mail gönderme</option>
                {templateOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className='form-text'>
                Şablonda {'{{interviewDate}}'}, {'{{interviewStartTime}}'}, {'{{meetingLink}}'} gibi parametreler
                kullanılabilir.
              </div>
            </div>
            <div className='col-md-5 d-flex align-items-center'>
              <label className='form-check form-check-custom form-check-solid mt-md-5'>
                <input
                  type='checkbox'
                  className='form-check-input'
                  checked={form.notifyCandidate}
                  disabled={!form.mailTemplateId}
                  onChange={(e) => set('notifyCandidate', e.target.checked)}
                />
                <span className='form-check-label'>Adaya da mail gönder</span>
              </label>
            </div>

            <div className='col-12'>
              <label className='form-label fw-bold'>Açıklama</label>
              <textarea
                rows={3}
                maxLength={5000}
                className='form-control'
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className='modal-footer'>
          <button type='button' className='btn btn-light' onClick={onClose}>
            İptal
          </button>
          <button type='submit' className='btn btn-primary' disabled={saving}>
            {saving ? <span className='spinner-border spinner-border-sm' /> : 'Mülakatı Planla'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
